import React, { useState, useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Activity, ChevronDown } from 'lucide-react';

interface ChartDataPoint {
    date: string;
    revenue: number;
    sales: number;
}

interface DashboardChartProps {
    data: ChartDataPoint[];
    loading: boolean;
}

type MetricType = 'revenue' | 'sales';

const CustomTooltip = ({ active, payload, label, currentMetric }: any) => {
    if (active && payload && payload.length) {
        const isRevenue = currentMetric === 'revenue';
        const val = payload[0].value;
        const formattedVal = isRevenue ? `R$ ${val.toFixed(2)}` : `${val} Vendas`;
        const color = isRevenue ? 'var(--primary)' : '#3b82f6';

        return (
            <div style={{
                background: 'rgba(20, 20, 20, 0.95)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                padding: '12px 16px',
                borderRadius: '8px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            }}>
                <p style={{ color: '#888', fontSize: '12px', marginBottom: '6px', fontWeight: 600 }}>{label}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: color }}></div>
                    <p style={{ color: color, fontSize: '16px', fontWeight: 700, margin: 0 }}>
                        {formattedVal}
                    </p>
                </div>
            </div>
        );
    }
    return null;
};

// Custom animated dot that pulses on the active point
const AnimatedActiveDot = (props: any) => {
    const { cx, cy, fill } = props;
    return (
        <g>
            <circle cx={cx} cy={cy} r={8} fill={fill} opacity={0.2}>
                <animate attributeName="r" from="6" to="16" dur="1.5s" repeatCount="indefinite" />
                <animate attributeName="opacity" from="0.4" to="0" dur="1.5s" repeatCount="indefinite" />
            </circle>
            <circle cx={cx} cy={cy} r={5} fill={fill} stroke="#fff" strokeWidth={2} />
        </g>
    );
};

export const DashboardChart: React.FC<DashboardChartProps> = ({ data, loading }) => {
    const [metric, setMetric] = useState<MetricType>('revenue');

    // Generate a unique key every time data changes to force Recharts to fully re-mount and re-animate
    const chartKey = useMemo(() => {
        return `${metric}-${data.length}-${Date.now()}`;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, metric]);

    const isRevenue = metric === 'revenue';
    const mainColor = isRevenue ? 'var(--primary)' : '#3b82f6';
    const rawColor = isRevenue ? '#ff6a00' : '#3b82f6'; // For SVG gradients (var() doesn't work in SVG)
    const fillGradientId = `grad-${metric}-${data.length}`;

    return (
        <div style={{
            background: 'var(--glass-bg)',
            backdropFilter: 'blur(12px)',
            border: 'var(--glass-border)',
            borderRadius: 'var(--radius)',
            padding: '24px',
            marginBottom: '24px',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
                <h3 style={{
                    fontSize: '18px',
                    fontWeight: 700,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    margin: 0
                }}>
                    <Activity size={20} color={mainColor} style={{ transition: 'color 0.5s ease' }} />
                    Evolução
                </h3>

                {/* Selector */}
                <div style={{ position: 'relative' }}>
                    <select
                        value={metric}
                        onChange={(e) => setMetric(e.target.value as MetricType)}
                        style={{
                            appearance: 'none',
                            background: 'rgba(255,255,255,0.05)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '20px',
                            color: '#fff',
                            padding: '8px 36px 8px 16px',
                            fontSize: '13px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            outline: 'none',
                            transition: 'all 0.3s ease'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                    >
                        <option value="revenue" style={{ background: '#1a1a1a', color: '#fff' }}>Faturamento</option>
                        <option value="sales" style={{ background: '#1a1a1a', color: '#fff' }}>Vendas</option>
                    </select>
                    <ChevronDown size={14} color="#888" style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                </div>
            </div>

            <div style={{ width: '100%', height: '320px', position: 'relative' }}>
                {loading ? (
                    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', gap: '10px', justifyContent: 'flex-end', paddingBottom: '20px' }}>
                        <div className="shimmer-loading" style={{ height: '40px', width: '20%', borderRadius: '4px', alignSelf: 'flex-end' }}></div>
                        <div className="shimmer-loading" style={{ height: '120px', width: '50%', borderRadius: '4px', alignSelf: 'flex-end' }}></div>
                        <div className="shimmer-loading" style={{ height: '200px', width: '80%', borderRadius: '4px', alignSelf: 'flex-end' }}></div>
                        <div className="shimmer-loading" style={{ height: '240px', width: '100%', borderRadius: '4px', alignSelf: 'flex-end' }}></div>
                    </div>
                ) : data && data.length > 0 ? (
                    <div
                        key={chartKey}
                        style={{
                            width: '100%',
                            height: '100%',
                            animation: 'chartFadeSlideIn 0.6s ease-out forwards'
                        }}
                    >
                        <style>{`
                            @keyframes chartFadeSlideIn {
                                from {
                                    opacity: 0;
                                    transform: translateY(20px);
                                }
                                to {
                                    opacity: 1;
                                    transform: translateY(0);
                                }
                            }
                        `}</style>
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart
                                data={data}
                                margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                            >
                                <defs>
                                    <linearGradient id={fillGradientId} x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={rawColor} stopOpacity={0.35} />
                                        <stop offset="50%" stopColor={rawColor} stopOpacity={0.1} />
                                        <stop offset="95%" stopColor={rawColor} stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.03)" vertical={false} />
                                <XAxis
                                    dataKey="date"
                                    stroke="#666"
                                    fontSize={12}
                                    tickLine={false}
                                    axisLine={false}
                                    dy={10}
                                />
                                <YAxis
                                    stroke="#666"
                                    fontSize={12}
                                    tickLine={false}
                                    axisLine={false}
                                    tickFormatter={(val) => isRevenue ? `R$ ${val}` : `${val}`}
                                />
                                <Tooltip
                                    content={<CustomTooltip currentMetric={metric} />}
                                    cursor={{ stroke: 'rgba(255,255,255,0.08)', strokeWidth: 1, strokeDasharray: '4 4' }}
                                />
                                <Area
                                    type="monotone"
                                    dataKey={metric}
                                    stroke={rawColor}
                                    strokeWidth={2.5}
                                    fillOpacity={1}
                                    fill={`url(#${fillGradientId})`}
                                    animationBegin={100}
                                    animationDuration={1500}
                                    animationEasing="ease-out"
                                    isAnimationActive={true}
                                    activeDot={<AnimatedActiveDot fill={rawColor} />}
                                    dot={false}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
                        Não há dados para este período.
                    </div>
                )}
            </div>
        </div>
    );
};
